import { useQuery } from "@apollo/client";
import { find, uniqBy } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CompetencyCriteriaNode,
  GetCareerRoleRelatedDataQueryQuery,
  GetCareerRoleRelatedDataQueryQueryVariables,
  ProfileCareerTabCareerTrackFragment,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { useLink } from "@components/link/link-helpers";
import { Select } from "@components/select/select";
import { assertEdgesNonNull } from "@helpers/helpers";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import getCareerRoleRelatedDataQuery from "../graphql/get-career-role-related-data-query";
import {
  mapCareerDevelopmentGoalsWithCompetencyIds,
  mapCompetencyAverageAssessmentAnswersById,
} from "./career-table-helpers";
import Competency from "./competency";
import NA from "./na";
import Responsibilities from "./responsibilities";

const TableRow = ({ label }: { label: string }) => {
  return (
    <div className="col-span-2 px-6 py-2 bg-gray-50 border-t">
      <div className="text-sm font-medium">{label}</div>
    </div>
  );
};

const ProfileCareerTable = ({
  profile,
  userRolesIds,
  selectedCareerTrack,
}: {
  profile: BasicUser & { isInCurrentUserReportingTree: boolean };
  userRolesIds: number[];
  selectedCareerTrack: ProfileCareerTabCareerTrackFragment;
}) => {
  const queryParams = useUrlQueryParams<{
    roleId: number | null;
  }>({
    roleId: null,
  });
  const paramRoleId = queryParams.roleId
    ? Number(queryParams.roleId)
    : undefined;
  const location = useLocation<TFLocationState>();
  const label = useLabel();
  const link = useLink();
  const allTrackRoles = useMemo(() => {
    return assertEdgesNonNull(selectedCareerTrack.roles);
  }, [selectedCareerTrack]);
  const userRoles = useMemo(() => {
    return allTrackRoles.filter((role) => userRolesIds.includes(role.id));
  }, [allTrackRoles, userRolesIds]);

  // Get suggested role by using role matching query param or first role.
  const suggestedCurrentRole =
    (paramRoleId && find(userRoles, { id: paramRoleId })) || userRoles[0];
  const [_selectedCurrentRoleId, setSelectedCurrentRoleId] = useState(
    suggestedCurrentRole?.id,
  );
  // in the case the career track changes and the state is not updated right away
  // we need to check if the selected current role is in the user roles
  const selectedCurrentRole =
    (_selectedCurrentRoleId &&
      find(userRoles, { id: _selectedCurrentRoleId })) ||
    userRoles[0];

  const currentCompetencies = useMemo(
    () =>
      selectedCurrentRole?.competencies
        ? assertEdgesNonNull(selectedCurrentRole.competencies)
        : [],
    [selectedCurrentRole],
  );
  const currentResponsibilities = useMemo(
    () =>
      selectedCurrentRole?.responsibilities
        ? assertEdgesNonNull(selectedCurrentRole.responsibilities)
        : [],
    [selectedCurrentRole],
  );

  // Next Role Data
  const futureRoles = useMemo(() => {
    return allTrackRoles.filter(
      (role) => selectedCurrentRole && role.level > selectedCurrentRole.level,
    );
  }, [allTrackRoles, selectedCurrentRole]);
  const suggestedNextLevelRole =
    (selectedCurrentRole &&
      find(futureRoles, { level: selectedCurrentRole.level + 1 })) ||
    futureRoles[0];
  const [_selectedNextRoleId, setSelectedNextRoleId] = useState<
    number | undefined
  >(suggestedNextLevelRole?.id);
  const selectedNextRole =
    (_selectedNextRoleId && find(futureRoles, { id: _selectedNextRoleId })) ||
    futureRoles[0];

  const nextCompetencies = useMemo(
    () =>
      selectedNextRole?.competencies
        ? assertEdgesNonNull(selectedNextRole.competencies)
        : [],
    [selectedNextRole],
  );
  const nextResponsibilities = useMemo(
    () =>
      selectedNextRole?.responsibilities
        ? assertEdgesNonNull(selectedNextRole.responsibilities)
        : [],
    [selectedNextRole],
  );

  // Merged competencies
  const mergedCompetencies = useMemo(() => {
    return uniqBy([...currentCompetencies, ...nextCompetencies], "id");
  }, [currentCompetencies, nextCompetencies]);

  const criteriaByCurrentCompetencyId = useMemo(() => {
    return currentCompetencies.reduce(
      (memo, competency) => {
        memo[competency.id] = assertEdgesNonNull(competency.criteria);
        return memo;
      },
      {} as Record<number, Pick<CompetencyCriteriaNode, "id" | "content">[]>,
    );
  }, [currentCompetencies]);

  const criteriaByNextCompetencyId = useMemo(() => {
    return nextCompetencies.reduce(
      (memo, competency) => {
        memo[competency.id] = assertEdgesNonNull(competency.criteria);
        return memo;
      },
      {} as Record<number, Pick<CompetencyCriteriaNode, "id" | "content">[]>,
    );
  }, [nextCompetencies]);

  // Fetch roles metadata
  const { data } = useQuery<
    GetCareerRoleRelatedDataQueryQuery,
    GetCareerRoleRelatedDataQueryQueryVariables
  >(getCareerRoleRelatedDataQuery, {
    skip: !selectedCurrentRole,
    variables: {
      profileId: profile.id,
      roleId: selectedCurrentRole?.id || 0,
    },
  });
  const { data: futureRoleData } = useQuery<
    GetCareerRoleRelatedDataQueryQuery,
    GetCareerRoleRelatedDataQueryQueryVariables
  >(getCareerRoleRelatedDataQuery, {
    skip: !selectedNextRole || !selectedCurrentRole,
    variables: {
      profileId: profile.id,
      roleId: selectedNextRole?.id || 0,
    },
  });

  const currentRoleDevelopmentGoals = useMemo(() => {
    return mapCareerDevelopmentGoalsWithCompetencyIds(data);
  }, [data]);

  const futureRoleDevelopmentGoals = useMemo(() => {
    return mapCareerDevelopmentGoalsWithCompetencyIds(futureRoleData);
  }, [futureRoleData]);

  // Calculate average assessment answers by competency/responsibility id
  const currentRoleAverageAssessmentAnswersByCompetencyId = useMemo(() => {
    return mapCompetencyAverageAssessmentAnswersById(data);
  }, [data]);
  const futureRoleAverageAssessmentAnswersByCompetencyId = useMemo(() => {
    return mapCompetencyAverageAssessmentAnswersById(futureRoleData);
  }, [futureRoleData]);

  // Update current role if career track is changed
  useEffect(() => {
    setSelectedCurrentRoleId(suggestedCurrentRole.id);
  }, [selectedCareerTrack]);

  // Update next role if current role is changed
  useEffect(() => {
    if (selectedNextRole?.id === selectedCurrentRole?.id) {
      setSelectedNextRoleId(
        suggestedNextLevelRole?.id !== selectedNextRole?.id
          ? suggestedNextLevelRole?.id
          : undefined,
      );
    }
  }, [selectedCurrentRole, selectedNextRole?.id, suggestedNextLevelRole?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("careerTrackId", `${selectedCareerTrack.id}`);
    if (selectedCurrentRole?.id) {
      params.set("roleId", `${selectedCurrentRole.id}`);
    }
    link.replace(`${location.pathname}?${params}`);
  }, [selectedCurrentRole?.id, location.pathname, location.search]);

  return (
    <div className="mt-6 text-sm border rounded-lg grid grid-cols-2 text-gray-700">
      {/* Role Picker */}
      <div className="col-span-1 p-6">
        <div className="text-xs font-medium text-gray-500">Current Role</div>
        <div className="mt-2">
          {selectedCurrentRole && userRoles.length > 0 ? (
            <Select<number>
              options={userRoles.map((role) => ({
                label: `${role.title} (Level: ${role.level})`,
                value: role.id,
              }))}
              value={selectedCurrentRole.id}
              onChange={(option) => setSelectedCurrentRoleId(option.value)}
            />
          ) : (
            <NA />
          )}
        </div>
      </div>
      <div className="col-span-1 p-6 border-l">
        <div className="text-xs font-medium text-gray-500">Future Role</div>
        {selectedNextRole?.id && futureRoles.length > 0 ? (
          <div className="mt-2">
            <Select<number>
              options={futureRoles.map((role) => ({
                label: `${role.title} (Level: ${role.level})`,
                value: role.id,
              }))}
              value={selectedNextRole.id}
              onChange={(option) => setSelectedNextRoleId(option.value)}
            />
          </div>
        ) : (
          <div className="mt-2 px-3 py-2 bg-gray-100 rounded-lg text-center text-gray-400 text-sm">
            There are no defined future {label("role", { pluralize: true })} in
            the {label("organization")}. Only Admins can create a role.
          </div>
        )}
      </div>

      {/* Competencies */}
      <TableRow label="Competencies" />
      {mergedCompetencies.map((competency) => (
        <Fragment key={competency.id}>
          <div className="col-span-1 border-t p-6">
            <Competency
              profile={profile}
              competency={competency}
              role={selectedCurrentRole}
              goals={currentRoleDevelopmentGoals.filter(({ competencyIds }) =>
                competencyIds.includes(competency.id),
              )}
              criteria={criteriaByCurrentCompetencyId[competency.id]}
              averageAssessmentAnswers={
                currentRoleAverageAssessmentAnswersByCompetencyId[competency.id]
              }
            />
          </div>
          <div className="col-span-1 border-t p-6 border-l">
            <Competency
              profile={profile}
              competency={competency}
              role={selectedNextRole}
              goals={futureRoleDevelopmentGoals.filter(({ competencyIds }) =>
                competencyIds.includes(competency.id),
              )}
              criteria={criteriaByNextCompetencyId[competency.id]}
              averageAssessmentAnswers={
                futureRoleAverageAssessmentAnswersByCompetencyId[competency.id]
              }
            />
          </div>
        </Fragment>
      ))}

      {/* Responsibilities */}
      <TableRow label="Responsibilities" />
      <>
        <div className="col-span-1 border-t p-6 flex flex-col gap-0">
          <Responsibilities
            careerRole={data?.careerRole}
            responsibilities={currentResponsibilities}
            profile={profile}
          />
        </div>

        <div className="col-span-1 border-t p-6 border-l flex flex-col gap-0">
          <Responsibilities
            responsibilities={nextResponsibilities}
            careerRole={futureRoleData?.careerRole}
            profile={profile}
          />
        </div>
      </>
    </div>
  );
};

export default ProfileCareerTable;
